import React, { useEffect, useState } from "react";
import type { AdminSessionUsersPage } from "../../../services/adminSessionsService";
import { formatDateTime } from "./sessionsModel";

export interface UserListProps {
  data: AdminSessionUsersPage | null;
  loading: boolean;
  error: string | null;
  query: string;
  selectedUserId: string | null;
  onSearch: (q: string) => void;
  onPage: (page: number) => void;
  onSelect: (userId: string) => void;
}

/** Left pane: every user who has at least one session, newest activity first. */
const UserList: React.FC<UserListProps> = ({
  data,
  loading,
  error,
  query,
  selectedUserId,
  onSearch,
  onPage,
  onSelect,
}) => {
  const [input, setInput] = useState(query);

  // Follow external changes (Back/Forward) without clobbering active typing.
  useEffect(() => setInput((prev) => (prev.trim() === query ? prev : query)), [query]);

  useEffect(() => {
    if (input.trim() === query) return;
    const timer = setTimeout(() => onSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input, query, onSearch]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="flex flex-col border border-outline-variant/30 rounded bg-surface-container min-h-[28rem]">
      <div className="p-3 border-b border-outline-variant/30">
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search email…"
          aria-label="Search users by email"
          className="w-full bg-surface-container-high border border-outline-variant/30 rounded px-3 py-2 text-sm font-body text-on-surface placeholder:text-slate-500 focus:outline-none focus:border-primary"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {error && <p className="p-4 text-sm text-error font-body">{error}</p>}
        {!error && loading && !data && (
          <p className="p-4 text-xs font-label uppercase tracking-widest text-slate-500">Loading…</p>
        )}
        {!error && data && data.users.length === 0 && (
          <p className="p-4 text-sm text-slate-500 font-body">
            {query ? "No users match that search." : "No one has started a session yet."}
          </p>
        )}
        {!error &&
          data?.users.map((u) => (
            <button
              key={u.userId}
              type="button"
              onClick={() => onSelect(u.userId)}
              className={`w-full text-left px-4 py-3 border-b border-outline-variant/10 transition-colors ${
                u.userId === selectedUserId
                  ? "bg-primary/10"
                  : "hover:bg-surface-container-high"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm font-body text-on-surface" title={u.email ?? u.stytchMemberId}>
                  {u.email ?? u.stytchMemberId}
                </span>
                <span className="shrink-0 text-xs font-label text-slate-500">{u.sessionCount}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2 mt-0.5">
                <span className="truncate text-xs font-body text-slate-500">
                  {u.organizationName ?? u.organizationId}
                </span>
                <span className="shrink-0 text-xs font-body text-slate-500">
                  {formatDateTime(u.lastActiveAt)}
                </span>
              </div>
            </button>
          ))}
      </div>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between p-3 border-t border-outline-variant/30 text-xs font-label uppercase tracking-widest text-slate-500">
          <button
            type="button"
            disabled={data.page <= 1 || loading}
            onClick={() => onPage(data.page - 1)}
            className="hover:text-on-surface disabled:opacity-40 disabled:hover:text-slate-500"
          >
            ‹ Previous
          </button>
          <span>
            Page {data.page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={data.page >= totalPages || loading}
            onClick={() => onPage(data.page + 1)}
            className="hover:text-on-surface disabled:opacity-40 disabled:hover:text-slate-500"
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};

export default UserList;
